import Vue from 'vue';
import rpcHeaders from '#/store/headers';
import { queryProjectClient, liveClient } from '#/clients';
import { LiveProjectStreamRequest } from '#/protobuf/service_live_subscriber_pb';
import { TaskPhase, TaskStatus } from '../../protobuf/enums_pb';
import { bus } from '../../toast/bus';

export function emptyState() {
  return {
    runLoopTimer: undefined,
    backoffMS: 250, // toggle how long to wait before reconnect
    projectStream: undefined,
    loadComplete: false,
    snapshots: {}, // this stores jobSnapshot objects
  };
}

export const getters = {
  loadComplete: (state) => state.loadComplete,
  snapshotById: (state) => (jobId) => state.snapshots[jobId],
  jobIds: (state) => Object.keys(state.snapshots),
};

export const mutations = {
  killProjectStream: (state) => {
    if (state.projectStream === undefined) {
      console.warn('killProjectStream called but no project stream is open');
      return;
    }
    console.warn('killing live project event stream');
    state.projectStream.cancel();
    state.projectStream = undefined;
  },
  resetState: (state) => {
    console.info('resetting live state');
    Object.assign(state, emptyState());
  },
  setLoadCompleted: (state) => {
    state.loadComplete = true;
  },
  setJobSnapshot: (state, jobSnapshot) => {
    Vue.set(state.snapshots, jobSnapshot.jobId, jobSnapshot);
  },
  setJobInfo: (state, jobInfo) => {
    if (jobInfo.details) {
      console.warn('skipping job details update in live stream');
    }
    if (jobInfo.snapshot) {
      Vue.set(state.snapshots, jobInfo.jobId, jobInfo.snapshot);
    }
  },
};

export const actions = {
  beginProjectStream: async ({
    state, commit, dispatch, rootState,
  }) => {
    if (state.projectStream !== undefined) {
      console.warn('cannot begin a project stream when one is already open.');
      return;
    }
    const headers = await rpcHeaders(rootState);
    const req = new LiveProjectStreamRequest();
    req.setListExistingSnapshots(true);
    state.projectStream = liveClient.liveProjectStream(req, headers);
    console.info('attempting to stream');
    state.projectStream.on('data', async (response) => {
      const r = response.toObject();
      // TODO: send initial response from server with no event if there are no live jobs.
      // TODO: else send an aggregate of existing job infos and snapshots from server to client on connection open.
      // TODO: would be nice to upsert store rather than overwrite
      const { event } = r;
      if (event) {
        console.info('project event:', event);
        if (event.comment) {
          const { comment } = event;
          console.info('received live comment', comment);
          // TODO: handle comment here
          const loginUser = rootState.user.userDetails;
          if (loginUser.userId !== comment.userInfo.userId) {
            bus.$emit('comment_receiving', comment);
            bus.$emit('comment_notification', comment);
          }
        }
        const { jobInfo } = event;
        if (jobInfo) {
          const { jobId, details, snapshot } = jobInfo;
          const { name, dateFinished } = details;
          commit('setJobInfo', jobInfo);
          if (snapshot) {
            if (snapshot.aggregateTaskPhase === TaskPhase.PHASE_DONE) {
              bus.$emit('job_notification', {
                title: 'job-notification', body: false, data: { name, dateFinished, jobId }, type: 'primary',
              });
            }
            if (snapshot.aggregateTaskStatus === TaskStatus.STATUS_ERRORED) {
              bus.$emit('job_notification', {
                title: 'Live Job Error', body: `Error in live job.\n id #${jobId}`, data: false, type: 'danger',
              });
            }
          }
        }
      }
      state.loadComplete = true;
    });

    state.projectStream.on('status', (status) => {
      console.log('Stream STATUS', status.code, status.details, status.metadata);
    });

    state.projectStream.on('error', (e) => {
      console.warn('Stream Error', e);
      commit('killProjectStream');
      setTimeout(() => {
        dispatch('beginProjectStream');
      }, state.backoffMS);
    });
  },
  listJobSnapshots: async ({ state, commit, rootState }, request) => {
    try {
      const headers = await rpcHeaders(rootState);
      return new Promise((resolve, reject) => {
        queryProjectClient.listJobSnapshots(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from live job snapshot list', err);
            return reject(err);
          }
          const r = response.toObject();
          console.info('loaded live jobs:', r);
          r.snapshotsList.forEach((snapshot) => {
            commit('setJobSnapshot', snapshot);
            // state.snapshots[s.jobId] = s;
            // state.snapshotMap.set(s.jobId, s);
            // state.machineIds.add(s.machineId); // add AFTER setting in map
          });
          state.loadComplete = true;
          return resolve(r);
        });
      });
    } catch (error) {
      console.log('caught error from live job snapshot list', error);
      return Promise.reject(error);
    }
  },
  getJobSnapshot: async ({ commit, rootState }, request) => {
    try {
      const headers = await rpcHeaders(rootState);
      return new Promise((resolve, reject) => {
        queryProjectClient.getJobSnapshot(request, headers, (err, response) => {
          if (err != null) {
            console.log('received err from live job snapshot list', err);
            return reject(err);
          }
          const r = response.toObject();
          const { snapshot } = r;
          commit('setJobSnapshot', snapshot);
          return resolve(snapshot);
        });
      });
    } catch (error) {
      console.log('caught error from live job snapshot get', error);
      return Promise.reject(error);
    }
  },
};

export default {
  namespaced: true,
  state: emptyState(),
  getters,
  mutations,
  actions,
};
